import React from 'react';
import PropTypes from 'prop-types';
import Question from '../../common/Question';
import TextAreaInput from '../../common/TextAreaInput';
import YesNoInputGroup from '../../common/YesNoInputGroup';
import DocumentUpload from '../../DocumentUpload';
import './ExperienceTab.scss';

const isDescEmpty = (desc) => !desc || !desc.trim();

const taExampleText = `While at Genzyme worked on Endocrine Ph III study and at Cubist
worked on Ph I Metabolism study.`;
const negativeTaExampleText = 'I do not have experience in cardiology but have worked with Class III devices in the pulmonary/respiratory space.';

const diseaseExampleText = 'I designed a Phase II protocol for triple negative breast cancer while at Roche.';
const negativeDiseaseExampleText = `I have not worked with sickle cell anemia specifically but have a strong understanding of blood diseases
and immune-mediated hematological disorders, as I worked for Celgene.`;

const prodRegExampleText = 'I have worked on 10+ IND studies and 4 BLA studies throughout my career across a range of disease areas.';
const negativeProdRegExampleText = 'I have supported clinical trials from phase 2b and 3 as well as post marketing but have not been directly involved with writing INDs.';

const therapiesExampleText = 'I have held several CMC positions for small molecules at Pfizer and J&J.';
const negativeTherapiesExampleText = `I do not have experience in In Vitro Diagnostics using biological samples.
However, I do have experience developing micro-electronics for biotech applications.`;

const aboutTextSuggestion = `[Project role] with [#] years of experience working for [small/medium/large/enterprise 
biotechnology/pharmaceutical/medical device/CRO] companies. With [list your top accomplishments] and expertise in 
[list Therapeutic Area(s) and/or Product Technologies/Modalities needed for the project that match your background], 
I am very interested in this project. Based on my experience, I believe that I would be a terrific fit.`;

const renderRequired = (render) => {
  if (render) {
    return (
      <div className="body-text-2 medium-weight with-error-text mb-2">
        <i className="fa fa-exclamation-triangle" />
        {' '}
        Required!
      </div>
    );
  }
  return null;
};

function ExperienceTab({
  project,
  proposal,
  onFormChange,
  showResumeQuestion,
  resume,
  viewResumeUrl,
  errors,
  csrfToken,
  uploadResumeUrl,
  userId,
}) {
  const {
    therapeutic_areas,
    disease_codes,
    regulatory_pathways,
    therapies,
  } = project;

  const displayFirstQuestion = (
    therapeutic_areas.length !== 0
    || disease_codes.length !== 0
    || regulatory_pathways.length !== 0
    || therapies.length !== 0
  );
  const numberIndexes = ['1. ', '2. ', '3. '];
  const letterIndexes = ['a. ', 'b. ', 'c. ', 'd. ', 'e. '];

  let therapeuticAreasError = null;
  if (isDescEmpty(proposal.therapeutic_areas_desc)) {
    therapeuticAreasError = errors.therapeutic_areas_desc;
  }

  let diseaseCodesError = null;
  if (isDescEmpty(proposal.disease_codes_desc)) {
    diseaseCodesError = errors.disease_codes_desc;
  }

  let regulatoryPathwaysError = null;
  if (isDescEmpty(proposal.regulatory_pathways_desc)) {
    regulatoryPathwaysError = errors.regulatory_pathways_desc;
  }

  let therapiesError = null;
  if (isDescEmpty(proposal.therapies_desc)) {
    therapiesError = errors.therapies_desc;
  }

  let aboutError = null;
  if (isDescEmpty(proposal.about_consultant)) {
    aboutError = errors.about_consultant;
  }

  let displayedTaExampleText = taExampleText;
  if (proposal.therapeutic_areas) {
    displayedTaExampleText = proposal.therapeutic_areas.some((p) => p.selected === 'No') ? negativeTaExampleText : taExampleText;
  }

  let displayedDiseaseExampleText = diseaseExampleText;
  if (proposal.disease_codes) {
    displayedDiseaseExampleText = proposal.disease_codes.some((p) => p.selected === 'No') ? negativeDiseaseExampleText : diseaseExampleText;
  }

  let displayedProdRegExampleText = prodRegExampleText;
  if (proposal.regulatory_pathways) {
    displayedProdRegExampleText = proposal.regulatory_pathways.some((p) => p.selected === 'No') ? negativeProdRegExampleText : prodRegExampleText;
  }

  let displayedTherapiesExampleText = therapiesExampleText;
  if (proposal.therapies) {
    displayedTherapiesExampleText = proposal.therapies.some((p) => p.selected === 'No') ? negativeTherapiesExampleText : therapiesExampleText;
  }

  return (
    <div>
      { displayFirstQuestion
        && (
        <div>
          <div className="body-text-2 medium-weight mb-5">
            { numberIndexes.shift() }
            Do you have experience in the following ...
          </div>

          { (therapeutic_areas.length !== 0)
            && (
            <div className="ms-4 mb-5">
              <Question
                title={`${letterIndexes.shift()}Therapeutic Areas`}
                location="proposal-edit"
              >
                <div className="ms-4">
                  <YesNoInputGroup
                    options={therapeutic_areas}
                    formKey="therapeutic_areas"
                    value={proposal.therapeutic_areas}
                    onAnswerChange={(val) => onFormChange('therapeutic_areas', val)}
                    errors={errors}
                  />
                  { proposal.therapeutic_areas
                    && proposal.therapeutic_areas.some((ta) => ta.selected !== null)
                    && (
                    <div className="mt-3 therapeutic-areas-description">
                      {renderRequired(therapeuticAreasError)}
                      <TextAreaInput
                        placeholder="Type your experience here."
                        onAnswerChange={(val) => onFormChange('therapeutic_areas_desc', val)}
                        value={proposal.therapeutic_areas_desc}
                        error={therapeuticAreasError}
                        exampleText={displayedTaExampleText}
                        textPromptClass={therapeuticAreasError && 'with-error-text'}
                        textAreaPrompt="Tell us specific examples of your experience as it relates
                        to the answers above."
                        required={null}
                      />
                    </div>
                    )}
                </div>
              </Question>
            </div>
            )}

          { disease_codes.length !== 0
            && (
            <div className="mb-5">
              { (disease_codes.length !== 0)
                && (
                <div className="ms-4 mb-5">
                  <Question
                    title={`${letterIndexes.shift()}Diseases & Indications`}
                    location="proposal-edit"
                  >
                    <div className="ms-4">
                      <YesNoInputGroup
                        options={disease_codes}
                        formKey="disease_codes"
                        value={proposal.disease_codes}
                        onAnswerChange={(val) => onFormChange('disease_codes', val)}
                        errors={errors}
                      />
                      { proposal.disease_codes
                        && proposal.disease_codes.some((dc) => dc.selected !== null)
                        && (
                        <div className="disease-codes-description">
                          {renderRequired(diseaseCodesError)}
                          <TextAreaInput
                            placeholder="Type your experience here."
                            onAnswerChange={(val) => onFormChange('disease_codes_desc', val)}
                            value={proposal.disease_codes_desc}
                            error={diseaseCodesError}
                            exampleText={displayedDiseaseExampleText}
                            textPromptClass={diseaseCodesError && 'with-error-text'}
                            textAreaPrompt="Tell us specific examples of your experience as it
                            relates to the answers above."
                            required={null}
                          />
                        </div>
                        )}
                    </div>
                  </Question>
                </div>
                )}
            </div>
            )}

          { regulatory_pathways.length !== 0
            && (
            <div className="mb-5 ms-5">
              <Question
                title={`${letterIndexes.shift()}Regulatory Pathways`}
                location="proposal-edit"
              >
                <div className="ms-4">
                  <YesNoInputGroup
                    options={regulatory_pathways}
                    formKey="regulatory_pathways"
                    value={proposal.regulatory_pathways}
                    onAnswerChange={(val) => onFormChange('regulatory_pathways', val)}
                    errors={errors}
                  />
                  { proposal.regulatory_pathways
                    && proposal.regulatory_pathways.some((rp) => rp.selected !== null)
                    && (
                    <div className="regulatory-pathways-description">
                      {renderRequired(regulatoryPathwaysError)}
                      <TextAreaInput
                        placeholder="Type your experience here."
                        onAnswerChange={(val) => onFormChange('regulatory_pathways_desc', val)}
                        value={proposal.regulatory_pathways_desc}
                        error={regulatoryPathwaysError}
                        exampleText={displayedProdRegExampleText}
                        textPromptClass={regulatoryPathwaysError && 'with-error-text'}
                        textAreaPrompt="Tell us specific examples of your experience as it relates
                        to the answers above."
                        required={null}
                      />
                    </div>
                    )}
                </div>
              </Question>
            </div>
            )}

          { therapies.length !== 0
            && (
            <div className="ms-4 mb-5">
              <Question
                title={`${letterIndexes.shift()}Product Technologies`}
                location="proposal-edit"
              >
                <div className="ms-4">
                  <YesNoInputGroup
                    options={therapies}
                    formKey="therapies"
                    value={proposal.therapies}
                    onAnswerChange={(val) => onFormChange('therapies', val)}
                    errors={errors}
                  />
                  { proposal.therapies && proposal.therapies.some((t) => t.selected !== null)
                    && (
                    <div className="therapies-description">
                      {renderRequired(therapiesError)}
                      <TextAreaInput
                        placeholder="Type your experience here."
                        onAnswerChange={(val) => onFormChange('therapies_desc', val)}
                        value={proposal.therapies_desc}
                        error={therapiesError}
                        exampleText={displayedTherapiesExampleText}
                        textPromptClass={therapiesError && 'with-error-text'}
                        textAreaPrompt="Tell us specific examples of your experience
                        as it relates to the answers above."
                        required={null}
                      />
                    </div>
                    )}
                </div>
              </Question>
            </div>
            )}
        </div>
        )}

      <div className="mb-4">
        <Question
          title={`${numberIndexes.shift()}Tell employers more about your professional past experience.`}
          error={aboutError}
          location="proposal-edit"
        >
          <div className="ms-4 input-container">
            <TextAreaInput
              placeholder="Type your summary here"
              rows={4}
              onAnswerChange={(val) => onFormChange('about_consultant', val)}
              value={proposal.about_consultant}
              error={aboutError}
              textPromptClass={aboutError && 'with-error-text'}
              className="proposal-text-area fs-hide proposal-past-experiences"
              textAreaPrompt=""
              required={null}
              textSuggestion={aboutTextSuggestion}
            />
          </div>
        </Question>
      </div>

      {showResumeQuestion
        && (
        <div className="resume-section mb-4">
          <div className="body-class-2 medium-weight mb-3">
            { numberIndexes.shift() }
            Resume
          </div>
          <div className="ms-4">
            <DocumentUpload
              buttonStyle="button"
              prompt="Upload Resume"
              viewDocumentUrl={viewResumeUrl}
              uploadDocumentUrl={uploadResumeUrl}
              userId={userId}
              documentType="resume"
              location="proposal-edit"
              csrfToken={csrfToken}
              document={resume}
              proposalResourceId={proposal.resource_id}
              saveOnUpload
            />
          </div>
        </div>
        )}
    </div>
  );
}

ExperienceTab.propTypes = {
  project: PropTypes.shape({}).isRequired,
  proposal: PropTypes.shape({}).isRequired,
  onFormChange: PropTypes.func.isRequired,
  showResumeQuestion: PropTypes.bool,
  resume: PropTypes.shape({}),
  viewResumeUrl: PropTypes.string.isRequired,
  uploadResumeUrl: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  errors: PropTypes.shape({}).isRequired,
  userId: PropTypes.string,
};

ExperienceTab.defaultProps = {
  showResumeQuestion: true,
  resume: null,
  userId: '',
};

export default ExperienceTab;
